// src/components/auth/jobs/auth/Settings.tsx
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Stack,
  Avatar,
  Button,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { UsersService } from '../../../../api/services/UsersService';
import { OpenAPI } from '../../../../api/core/OpenAPI';
import { useAuth } from '../../../../context/AuthContext'; // ⬅️ Header frissítéséhez

const API_BASE = OpenAPI.BASE || 'http://localhost:5051';
const joinUrl = (base: string, path: string) => base.replace(/\/+$/, '') + path;
const resolveAvatarBase = (url?: string | null) =>
  !url
    ? '/profile-placeholder.png'
    : url.startsWith('http')
    ? url
    : joinUrl(API_BASE, url);

// cache-buster: mindig új URL-t adunk vissza, így a böngésző nem a cache-t használja
const withBust = (url: string, seed: number) =>
  url.includes('?') ? `${url}&v=${seed}` : `${url}?v=${seed}`;

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('/profile-placeholder.png');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // cache-buster seed – mentés után frissítjük
  const [bust, setBust] = useState<number>(Date.now());

  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false,
    msg: '',
    sev: 'success',
  });

  const { refreshMe } = useAuth(); // ⬅️ Header/Context azonnali frissítése

  // Betöltés
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await UsersService.getApiUsersMe();
        if (!mounted) return;
        setEmail(me.email ?? '');
        setDisplayName(me.displayName ?? '');
        const base = resolveAvatarBase(me.profileImageUrl);
        setAvatarUrl(withBust(base, Date.now())); // első betöltésnél is adjunk bust-ot
      } catch {
        setSnack({ open: true, msg: 'Nem sikerült betölteni a profilt.', sev: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Fájl előnézet
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const initials = useMemo(() => {
    const src = displayName?.trim() || email?.split('@')[0] || 'U';
    return src
      .split(/[ ._-]+/g)
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }, [displayName, email]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1) display name
      await UsersService.putApiUsersMe({ displayName: displayName.trim() || null });

      // 2) avatar
      if (file) {
        // @ts-expect-error - a backend multipart 'file' kulcsot vár
        await UsersService.postApiUsersMeAvatar({ file });
      }

      // 3) friss állapot lekérése és cache-bust (helyi UI-hoz)
      const me = await UsersService.getApiUsersMe();
      setDisplayName(me.displayName ?? '');

      const base = resolveAvatarBase(me.profileImageUrl);
      const newSeed = Date.now();
      setBust(newSeed);
      setAvatarUrl(withBust(base, newSeed)); // <-- új query param, garantáltan friss kép

      // preview és file ürítése
      setFile(null);
      setPreview(null);

      // 4) globális user frissítés → Header azonnal reagál
      await refreshMe();

      setSnack({ open: true, msg: 'Profil frissítve.', sev: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Mentés sikertelen.', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
        Beállítások
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: '1px solid',
          borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,.12)' : 'rgba(0,0,0,.12)'),
          borderRadius: 3,
          backdropFilter: 'blur(6px)',
        }}
      >
        <Stack spacing={2}>
          {/* Email (read-only) */}
          <TextField label="Email" value={email} disabled fullWidth />

          {/* Display name (editable) */}
          <TextField
            label="Megjelenített név"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            placeholder="Add meg, hogyan jelenjen meg a neved"
          />

          <Divider sx={{ my: 1 }} />

          {/* Avatar */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={preview || avatarUrl} sx={{ width: 80, height: 80, fontSize: 24 }}>
              {initials}
            </Avatar>

            <Button component="label" variant="outlined">
              Új profilkép kiválasztása
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>

            {file && (
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {file.name}
              </Typography>
            )}
          </Stack>

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              Mentés
            </Button>
            {file && (
              <Button
                variant="text"
                onClick={() => {
                  setFile(null);
                  setPreview(null);
                }}
                disabled={saving}
              >
                Kép kiválasztás törlése
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
